import { create, StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import { ClientError } from '../../api/apiError';
import restClient from '../../api/Rest';
import { logout as restLogout } from '../../api/Rest/user';
import {
  login as restLogin,
  register as restRegister,
} from '../../api/Rest/users';
import { InternetKeychain } from '../../constants/keychain';
import { UserId } from '../../models/Base';
import {
  LoginResult,
  LogoutResult,
  RegisterResult,
  SessionStoreAction,
  SessionStoreState,
  SessionStoreStateJson,
  SessionTokens,
} from '../../models/SessionStore';
import { StorageValue } from '../../models/Store';
import {
  StoreLifecycle,
  StoreLifecycleAction,
} from '../../models/Store/StoreLifecycle';
import { UserRole } from '../../models/UserStore/Profile';
import { UserJson } from '../../models/UserStore/UserStore';
import { dateTimeReviver } from '../../utils/json';
import { useFileStore } from '../FileStore';
import { useSpaceStore } from '../SpaceStore';
import { useUserStore } from '../UserStore';
import { useRewardStore } from '../RewardStore';
import { RegionsCode } from '../../constants/region';

type InitializeParams =
  | [userId: string, accessToken: string, refreshToken: string]
  | [undefined, undefined, undefined];
type Store = StoreLifecycleAction<InitializeParams> &
  SessionStoreState &
  SessionStoreAction;

const setSessionTokens = (userId: UserId, tokens: SessionTokens) => {
  localStorage.setItem(
    `${userId}_${InternetKeychain.RefreshToken}`,
    tokens.refreshToken,
  );
  localStorage.setItem(
    `${userId}_${InternetKeychain.AccessToken}`,
    tokens.accessToken,
  );
};

const getSessionTokens = (userId?: UserId | null) => {
  const accessToken = localStorage.getItem(
    `${userId}_${InternetKeychain.AccessToken}`,
  );
  const refreshToken = localStorage.getItem(
    `${userId}_${InternetKeychain.RefreshToken}`,
  );

  return { accessToken, refreshToken };
};

const resetSessionTokens = (userId?: UserId) => {
  localStorage.removeItem(`${userId}_${InternetKeychain.AccessToken}`);
  localStorage.removeItem(`${userId}_${InternetKeychain.RefreshToken}`);
};

const lifecycle = new StoreLifecycle<InitializeParams>('SessionStore');

const initializer: StateCreator<Store, [], []> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    async (userId, accessToken, refreshToken) => {
      if (userId && accessToken && refreshToken) {
        // logined

        await setup(accessToken);
      } else {
        await Promise.all([
          useFileStore.getState().initialize(),
          useSpaceStore.getState().initialize(),
          useRewardStore.getState().initialize(),
        ]);
      }
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    async () => {
      // other stores
      await Promise.all([
        useFileStore.getState().shutdown(),
        useSpaceStore.getState().shutdown(),
        useUserStore.getState().shutdown(),
        useRewardStore.getState().shutdown(),
      ]);

      // rest
      restClient.setAuthorizationToken(null);

      // tokens
      resetSessionTokens();

      // clear data
      set({
        activeUserId: null,
        tokenExpiredAt: null,
        activeRegion: RegionsCode.US,
        scrolledPos: null,
      });
    },
    () => set((state) => ({ ...state })),
  );

  const register: SessionStoreAction['register'] = async (params, options) => {
    const { ok, data, error } = await restRegister(params, options);
    let result = RegisterResult.Error;
    let password;

    if (ok) {
      if (data.user.profile.role === UserRole.WaitingListUser) {
        // Use Main account to store the user
        useUserStore.getState().setupActiveUserInfo(undefined, data.user);
        return { result: RegisterResult.PreRegistered };
      }

      await cleanup();
      setSession(data.user, data.tokens);
      await setup(data.tokens.accessToken);
      result = RegisterResult.Success;
      if (data.password) {
        password = data.password;
      }
    } else if (error instanceof ClientError) {
      if (error.haveError({ code: RegisterResult.EmailRegistered })) {
        result = RegisterResult.EmailRegistered;
      } else if (error.haveError({ code: RegisterResult.UsernameRegistered })) {
        result = RegisterResult.UsernameRegistered;
      } else if (error.haveError({ code: RegisterResult.ValidationError })) {
        result = RegisterResult.ValidationError;
      }
    }
    return { result, password };
  };

  const login: SessionStoreAction['login'] = async (params, options) => {
    const { ok, data, error } = await restLogin(params, options);
    let result = LoginResult.Error;

    if (ok) {
      if ('mainAccount' in data) {
        useUserStore
          .getState()
          .setupActiveUserInfo(undefined, data.mainAccount, data.subaccounts);
        result = LoginResult.HaveSubAccounts;
      } else {
        await cleanup();
        setSession(data.user, data.tokens);
        await setup(data.tokens.accessToken);
        result = data.isReactivated
          ? LoginResult.SuccessReactivated
          : LoginResult.Success;
      }
    } else if (error instanceof ClientError) {
      if (error.haveError({ code: LoginResult.InvalidCredentials })) {
        result = LoginResult.InvalidCredentials;
      } else if (error.haveError({ message: LoginResult.AvailableOauth2Id })) {
        result = LoginResult.AvailableOauth2Id;
      }
    }
    return result;
  };

  const logout: SessionStoreAction['logout'] = async (
    forceLogout = false,
    options,
  ) => {
    if (!forceLogout) {
      // XXX: not checking download store undone task, still call the function to get warning logs
      // useDownloadStore.getState().hasUndoneTasks();
      // if (useUploadStore.getState().hasUndoneTasks()) {
      //   return LogoutResult.HasUndoneTasks;
      // }
    }
    const { ok } = await restLogout({}, options);
    await shutdown();
    return ok ? LogoutResult.Success : LogoutResult.Error;
  };

  // const refreshTokens: SessionStoreAction['refreshTokens'] = async () => {
  //   // 3 days = 1/10 access token lifespan
  //   if (
  //     (get().tokenExpiredAt?.getTime() ?? 0) - new Date().getTime() <=
  //     3 * TimeToSecond.Day * 1000
  //   ) {
  //     const { ok: requestOk, data } = await requestTokenRefresh();
  //     if (requestOk) {
  //       const { ok: confirmOk } = await confirmTokenRefresh(data);
  //       if (confirmOk) {
  //         const activeUserId = get().activeUserId;
  //         if (activeUserId) {
  //           set({ tokenExpiredAt: new Date(data.pendingTokens.exp * 1000) });
  //           await setSessionTokens(activeUserId, data.pendingTokens);
  //           restClient.setAuthorizationToken(data.pendingTokens.accessToken);
  //           webSocketClient.setToken(data.pendingTokens.refreshToken);
  //         }
  //       }
  //     }
  //   }
  // };

  const setSession = (user: UserJson, tokens: SessionTokens): void => {
    // Fixme
    useUserStore.getState().setupActiveUserInfo(user);

    // set data
    set({
      activeUserId: user.account.userId,
      tokenExpiredAt: new Date(tokens.exp * 1000),
      activeRegion: (user.profile.region as RegionsCode) ?? RegionsCode.US,
      scrolledPos: null,
    });

    // tokens
    setSessionTokens(user.account.userId, tokens);
  };

  const cleanup = async () => {
    try {
      await Promise.all([
        useFileStore.getState().shutdown(),
        useSpaceStore.getState().shutdown(),
        useUserStore.getState().shutdown(),
        useRewardStore.getState().shutdown(),
      ]);
    } catch {
      // do nothing
    }
  };

  const setup = async (accessToken: string) => {
    // onesignal
    // OneSignal.setExternalUserId(
    //   Config.ONESIGNAL_EXTERNAL_USER_ID_PREFIX + userId,
    // );

    // rest
    restClient.setAuthorizationToken(accessToken);

    // first round: non task store
    await Promise.all([
      useFileStore.getState().initialize(),
      useSpaceStore.getState().initialize(),
      useUserStore.getState().initialize(),
      useRewardStore.getState().initialize(),
    ]);
  };

  return {
    initialize,
    shutdown,
    activeUserId: null,
    tokenExpiredAt: null,
    activeRegion: RegionsCode.US,
    scrolledPos: null,
    register,
    login,
    logout,
    setActiveRegion: (region: RegionsCode) =>
      set((s) => ({ ...s, activeRegion: region })),
    isLoggedIn: () => get().activeUserId !== null,
    setScrollPos: (pos: number | null) =>
      set((s) => ({ ...s, scrolledPos: pos })),
  };
};

const persistOptions: PersistOptions<Store, Partial<SessionStoreState>> = {
  name: 'session-storage',
  // getStorage: () => AsyncStorage,
  deserialize: (str: string): StorageValue<Partial<SessionStoreState>> => {
    try {
      const { state, version } = JSON.parse(
        str,
        dateTimeReviver,
      ) as StorageValue<SessionStoreStateJson>;
      return {
        state: {
          activeUserId: state.activeUserId,
          tokenExpiredAt: state.tokenExpiredAt,
          activeRegion: state.activeRegion ?? RegionsCode.US,
        },
        version,
      };
    } catch (err) {
      console.error('Fail to deserialize session-storage', str);
      console.error(err);
      return { state: {} };
    }
  },
  onRehydrateStorage: () => {
    const onFinishHydration = async (state: Store | undefined) => {
      lifecycle.finishHydration();
      const { accessToken, refreshToken } = getSessionTokens(
        state?.activeUserId,
      );
      if (state?.activeUserId && accessToken && refreshToken) {
        await state.initialize(
          state.activeUserId,
          accessToken,
          refreshToken,
          // accessToken.password,
          // refreshToken.password,
        );
      } else {
        await state?.initialize(undefined, undefined, undefined);
        // TODO: handle rehydrate error case by shutdown?
      }
    };
    return (persistedState) => {
      onFinishHydration(persistedState).catch(console.error);
    };
  },
};

export const useSessionStore = create<Store>()(
  persist(initializer, persistOptions),
);
